
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { ClicksByCategory, ClicksByProduct } from '@/hooks/product/useProductAnalytics';
import { ArrowUp, ArrowDown } from 'lucide-react';

interface AnalyticsOverviewProps {
  totalClicks: number;
  previousTotalClicks?: number;
  clicksGrowth?: number;
  topCategory: ClicksByCategory | null;
  topProduct: ClicksByProduct | null;
}

export function AnalyticsOverview({ 
  totalClicks, 
  previousTotalClicks = 0, 
  clicksGrowth = 0, 
  topCategory, 
  topProduct 
}: AnalyticsOverviewProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Total Clicks</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalClicks}</div>
          <div className="flex items-center mt-1 text-xs">
            {clicksGrowth >= 0 ? (
              <ArrowUp className="h-3 w-3 text-emerald-500 mr-1" />
            ) : (
              <ArrowDown className="h-3 w-3 text-rose-500 mr-1" />
            )}
            <span className={clicksGrowth >= 0 ? "text-emerald-500" : "text-rose-500"}>
              {Math.abs(clicksGrowth)}%
            </span>
            <span className="text-muted-foreground ml-1">vs previous period</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Previous: {previousTotalClicks}
          </p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Top Category</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {topCategory ? topCategory.category : 'No data'}
          </div>
          {topCategory && topCategory.growth !== undefined && (
            <div className="flex items-center mt-1 text-xs">
              {topCategory.growth >= 0 ? (
                <ArrowUp className="h-3 w-3 text-emerald-500 mr-1" />
              ) : (
                <ArrowDown className="h-3 w-3 text-rose-500 mr-1" />
              )}
              <span className={topCategory.growth >= 0 ? "text-emerald-500" : "text-rose-500"}>
                {Math.abs(topCategory.growth)}%
              </span>
              <span className="text-muted-foreground ml-1">vs previous period</span>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            {topCategory ? `${topCategory.clicks} clicks` : '0 clicks'}
            {topCategory?.previous_clicks !== undefined && ` (Previous: ${topCategory.previous_clicks})`}
          </p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Top Product</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold truncate">
            {topProduct ? topProduct.product_name : 'No data'}
          </div>
          {topProduct && topProduct.growth !== undefined && (
            <div className="flex items-center mt-1 text-xs">
              {topProduct.growth >= 0 ? (
                <ArrowUp className="h-3 w-3 text-emerald-500 mr-1" />
              ) : (
                <ArrowDown className="h-3 w-3 text-rose-500 mr-1" />
              )}
              <span className={topProduct.growth >= 0 ? "text-emerald-500" : "text-rose-500"}>
                {Math.abs(topProduct.growth)}%
              </span>
              <span className="text-muted-foreground ml-1">vs previous period</span>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            {topProduct ? `${topProduct.clicks} clicks` : '0 clicks'}
            {topProduct?.previous_clicks !== undefined && ` (Previous: ${topProduct.previous_clicks})`}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
